/* src/Holidays.jsx */
import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { companyID } from "./../../../auth";
import api from "../../../api/axios";
import { AddHoliday } from "../../../components/modelpopup/AddHoliday";
import UploadHoliday from "../../../components/modelpopup/UploadHoliday";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import EditHolidayModal from "../../../components/modelpopup/EditHolidayModal";
import * as XLSX from "xlsx";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import Loading from "../../../components/Loading";
import { useRole } from "../../../context/RoleContext";

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [configurationHolidays, setConfigurationHolidays] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [holidayListDownloaded, setHolidayListDownloaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();
  const shouldRefetch = useRef(false);
  const { role, rolesConfig } = useRole();
  const holidayPermissions = rolesConfig[role]?.pages["holidays"] || [];
  const canEdit = holidayPermissions.includes("edit");
  const canDelete = holidayPermissions.includes("delete");

  const currentYear = new Date().getFullYear();

  const handleEventDidMount = (info) => {
    info.el.style.cursor = canEdit ? "pointer" : "default";
    tippy(info.el, {
      content: `<strong>${info.event.extendedProps.name}</strong>`,
      allowHTML: true,
    });
  };

  const handleEventClick = ({ event }) => {
    if (!canEdit) return;
    setSelectedHoliday({
      companyHolidayId: event.extendedProps.companyHolidayId,
      title: event.title,
      date: event.startStr,
    });
    setShowEditModal(true);
  };

  const fetchHolidays = useCallback(() => {
    setLoading(true);
    const company_id = companyID();
    api
      .get(`holidays/${company_id}/${currentYear}`)
      .then((res) => {
        const holidaysData = res?.data?.data?.holidays || [];

        // Remove duplicates based on occasion and date
        const uniqueHolidays = holidaysData.reduce((acc, holiday, index) => {
          const dateStr =
            holiday?.holiday_date &&
            formatDateInTimezone(holiday?.holiday_date);
          const key = `${holiday.occasion}-${dateStr}`;

          if (!acc.some((item) => `${item.title}-${item.date}` === key)) {
            acc.push({
              id: `holiday-${holiday.company_holiday_id || index}`,
              title: holiday.occasion,
              date: dateStr,
              allDay: true,
              extendedProps: {
                name: holiday.occasion,
                companyHolidayId: holiday.company_holiday_id,
              },
            });
          }
          return acc;
        }, []);

        setHolidays(uniqueHolidays);
      })
      .catch((err) => console.error("Error fetching holidays:", err))
      .finally(() => setLoading(false));
  }, [currentYear]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedHoliday(null);
  };

  const handleHolidaySaved = () => {
    closeEditModal();
    fetchHolidays();
  };

  const openDeleteModal = () => {
    if (!canDelete) return;
    setShowEditModal(false);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedHoliday(null);
  };

  const handleDeleteHoliday = async () => {
    if (!canDelete || !selectedHoliday) return;
    await api.delete(`company-holidays/${selectedHoliday.companyHolidayId}`);
    fetchHolidays();
    closeDeleteModal();
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
    // Only refetch if holidays were actually uploaded
    if (shouldRefetch.current) {
      fetchHolidays();
      shouldRefetch.current = false;
    }
  };

  const markForRefetch = () => {
    shouldRefetch.current = true;
  };

  const fetchConfigurationHolidays = async () => {
    try {
      const res = await api.get(`configuration-holidays`);
      const holidays = res?.data?.data.map((holiday) => ({
        occasion: holiday.occasion,
        holidayDates: "",
        year: "",
      }));
      setConfigurationHolidays(holidays);
    } catch (err) {
      console.error("Error fetching configuration holidays:", err);
    }
  };

  const handleDownload = async () => {
    await fetchConfigurationHolidays();
    const worksheetData = configurationHolidays.map((holiday) => ({
      Occasion: holiday.occasion,
      HolidayDates: holiday.holidayDates,
      Year: holiday.year,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Holidays");
    XLSX.writeFile(workbook, "Holidays.xlsx");
    setHolidayListDownloaded(true);
  };

  if (loading) {
    return <Loading size="xl" variant="dots" text="" />;
  }

  return (
    <div
      className={
        location.pathname.includes("leave-dashboard") ? "" : "page-wrapper"
      }
    >
      <div
        className={
          location.pathname.includes("leave-dashboard")
            ? ""
            : "content container-fluid"
        }
      >
        {!location.pathname.includes("leave-dashboard") && (
          <Breadcrumbs
            maintitle="Holidays"
            title="Dashboard"
            subtitle="Holidays"
            name="Add Holidays"
            openModal={() => setShowUploadModal(true)}
            page="holidays"
            action="create"
          />
        )}
        <div className="card row">
          <div className="card-body col-md-12">
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              events={holidays}
              height="auto"
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: "",
              }}
              eventDidMount={handleEventDidMount}
              eventClick={handleEventClick}
              editable={false}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
            />
          </div>
        </div>
      </div>
      <AddHoliday />
      {showUploadModal && (
        <UploadHoliday
          show={showUploadModal}
          setShow={handleUploadModalClose}
          handleDownload={handleDownload}
          holidayListDownloaded={holidayListDownloaded}
          setHolidayListDownloaded={setHolidayListDownloaded}
          markForRefetch={markForRefetch}
        />
      )}
      <EditHolidayModal
        holiday={selectedHoliday}
        show={showEditModal}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={closeEditModal}
        onSaved={handleHolidaySaved}
        onDelete={openDeleteModal}
      />
      <DeleteModal
        Name="Delete Holiday"
        show={showDeleteModal}
        confirmDelete={handleDeleteHoliday}
        onClose={closeDeleteModal}
      />
    </div>
  );
};

export default Holidays;
